/**
 * Componente Complaint
 * 
 * Este componente permite gestionar las quejas, incluyendo la visualización de detalles de cada queja.
 * Utiliza `DataTable` para mostrar las quejas en una tabla.
 * 
 * Estado:
 * - selectedComplaint: Queja seleccionada para ver detalles.
 * 
 * Funciones:
 * - selectComplaintData: Selecciona una queja para ver detalles.
 * - handleUpdateSuccess: Maneja el éxito de la actualización de la queja.
 * - actionButton: Renderiza los botones de acción en la tabla.
 * 
 * Hooks:
 * - useFetch: Hook personalizado para obtener datos de la API.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import * as Icon from 'react-feather';
import ComplaintModal from '../common/modals/complaint-modal';
import useFetch from '../../../core/hooks/api/useFetch';
import API_ENDPOINTS from '../../../core/constant/API_ENDPOINTS';
import DataTableWrapper from '../common/dataTable/dataTableWrapper';
import ConfirmActionModal from '../../components/modal/ConfirmActionModal';
import SuccessModal from '../../components/modal/SuccessModal';
import ErrorModal from '../../components/modal/ErrorModal';
import { postData } from '../../../core/services/api';
import { extractEmail } from '../../../core/utils/utils';

const Complaint = () => {
  const [selectedComplaint, setSelectedComplaint] = useState<any>();
  const [emailModalVisible, setEmailModalVisible] = useState<boolean>(false);
  const [complaintToEmail, setComplaintToEmail] = useState<any>(null);
  const [sendingEmail, setSendingEmail] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<any>(null);
  const [successModalVisible, setSuccessModalVisible] = useState<boolean>(false);
  const { data, error } = useFetch(API_ENDPOINTS.complaint);

  const selectComplaintData = (data: any) => {
    setSelectedComplaint(data);
  };

  const viewButton = (rowData: any) => {
    const handleViewClick = () => {
      selectComplaintData(rowData);
    };

    return (
      <Link
        className="delete-table me-2"
        onClick={handleViewClick}
        to="#"
        data-bs-toggle="modal"
        data-bs-target="#complaint-modal"
      >
        <Icon.Eye className="react-feather-custom" />
      </Link>
    );
  };

  const sendEmailAction = (rowData: any) => {
    setComplaintToEmail(rowData);
    setEmailError(null);
    setEmailModalVisible(true);
  };

  const handleSendEmail = async () => {
    if (!complaintToEmail || !complaintToEmail.phoneOrEmail || !complaintToEmail.id) return;

    const email = extractEmail(complaintToEmail.phoneOrEmail);
    const complaintId = complaintToEmail.id;

    // Validación de correo electrónico
    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!isEmail) {
      setEmailModalVisible(false);
      setEmailError("El contacto proporcionado no es un correo electrónico válido.");
      return;
    }

    try {
      setSendingEmail(true);
      setEmailError(null);

      const endpoint = `complaints/${complaintId}/send-email?email=${email}`;
      await postData(endpoint, {});
      setEmailModalVisible(false);
      setSuccessModalVisible(true);
    } catch (error) {
      console.error('Error al enviar el correo:', error);
      setEmailError(error);
    } finally {
      setSendingEmail(false);
    }
  };

  const closeEmailModal = () => {
    setEmailModalVisible(false);
    setComplaintToEmail(null);
  };

  const closeSuccessModal = () => {
    setSuccessModalVisible(false);
  };

  return (
    <div className="page-wrapper page-settings">
      <div className="content mb-4">
        <div className="content-page-header content-page-headersplit mb-4">
          <h5>Quejas</h5>
        </div>
        <DataTableWrapper
          data={data || []}
          columns={[
            {
              field: "fullName",
              header: "Nombre",
              headerIcon: <Icon.User size={16} />
            },
            {
              field: "phoneOrEmail",
              header: "Contacto",
              headerIcon: <Icon.Phone size={16} />
            },
            {
              field: "dniOrCe",
              header: "Documento",
              headerIcon: <Icon.FileText size={16} />
            },
            {
              field: "order",
              header: "Orden",
              headerIcon: <Icon.ShoppingBag size={16} />
            },
            {
              field: "action",
              header: "Acciones",
              body: viewButton
            }
          ]}
          lazy={true}
          paginator={false}
          onCustomAction={sendEmailAction}
          customIcon={Icon.Mail}
          customTitle="Enviar correo"
          emptyMessage={error ? "Error al cargar los datos" : "No hay quejas disponibles"}
          showGridlines={true}
        />
      </div>
      <ComplaintModal selectedComplaint={selectedComplaint} />

      <ConfirmActionModal
        show={emailModalVisible}
        onClose={closeEmailModal}
        onConfirm={handleSendEmail}
        title="Confirmar envío de correo"
        text={`¿Estás seguro de que deseas enviar un correo a ${complaintToEmail?.phoneOrEmail || ''}?`}
        loading={sendingEmail}
        error={emailError}
      />

      <ErrorModal
        show={emailError==="El contacto proporcionado no es un correo electrónico válido."}
        onClose={() => setEmailError(null)}
        onConfirm={closeEmailModal}
        title="Error"
        text={emailError || "Ocurrió un error al enviar el correo"}
        footer={(
          <button className="btn btn-danger" onClick={() => setEmailError(null)}>
            Cerrar
          </button>
        )}
      />

      <SuccessModal
        show={successModalVisible}
        onClose={closeSuccessModal}
        onConfirm={closeSuccessModal}
        title="Éxito"
        text="Correo enviado exitosamente"
      />
    </div>
  );
};

export default Complaint;
